<template>
  <article
    class="jps-item-detail"
    :class="{
      'has-image': hasImage,
      'has-iiif': item.common.iiifUrl,
      'page-jps': isJps,
      'page-hina': isHina,
    }"
    v-if="item"
  >
    <header>
      <div
        v-if="hasImage && !isDigiDepoContent"
        class="jps-item-img-container layout-col ui-item-view-container"
      >
        <ClientOnly>
          <UiIiifViewer
            v-if="viewerInfo"
            :image="viewerInfo"
            mode="none"
            @url="setUrl"
          />
          <div
            v-else
            class="jps-item-img"
          >
            <BaseImage
              :src="item.common.thumbnailUrl[0]"
              :error-alt="itemDetailNoImage"
            />
          </div>
        </ClientOnly>
      </div>
      <div v-if="isDigiDepoContent" class="jps-item-iframe-container layout-col ui-item-view-container">
        <ClientOnly>
          <iframe
            class="jps-item-iframe"
            :src="item.common.linkUrl"
          />
        </ClientOnly>
      </div>
      <BaseContainer>
        <div class="jps-db-notification ui-item-view-db-notification" v-if="showDbNotification">
          <div class="level is-mobile ui-item-view-db-notification-item">
            <BaseIcon name="mdi/alpha-i-circle-outline" size="size-2" />
            <p class="has-text-weight-bold is-size-7-touch ui-item-view-db-notification-text">
              {{ ls(db.notification) }}
            </p>
          </div>
        </div>
        <div class="jps-db-notification ui-item-view-db-notification" v-if="test">
          <div class="level is-mobile ui-item-view-db-notification-item">
            <BaseIcon name="mdi/alpha-i-circle-outline" size="size-2" />
            <p class="level is-mobile">
              この画面はアイテム詳細のテスト画面です。
            </p>
          </div>
        </div>
        <div class="jps-item-information">
          <div class="jps-item-title level ui-item-view-information-header">
            <h1 class="jps-page-title">
              {{ li(item) }}
              <span class="jps-page-title-yomi">{{
                isJa ? item.common.titleEn : item.common.title
              }}</span>
              <span
                class="jps-page-title-yomi"
                v-if="item.common.titleYomi"
                >{{ item.common.titleYomi }}</span
              >
            </h1>
            <div class="jps-item-url ui-item-view-information-url" v-if="item.common.linkUrl">
              <UiButtonLinkRound
                :url="item.common.linkUrl"
                styled="primary"
                size="small"
              >
                {{
                  ls(dapLangGlobal.openInSource)
                }}
              </UiButtonLinkRound>
            </div>
          </div>
          <div class="jps-tag-list jps-link-tags">
            <UiTagLink
              :searchscroll="true"
              v-for="c in item.common.category"
              :key="c"
              :url="categorySearch(c)"
              text-overflow="wrap"
              styled="primary"
            >{{ lc(categoryCode, c) }}</UiTagLink>
            <UiTagLink
              :searchscroll="true"
              v-if="isJps"
              v-for="c in rdfTypes"
              :key="c"
              :url="typeSearch(c)"
              text-overflow="wrap"
              styled="secondary"
            >{{ lc(rdfTypeCode, c) }}</UiTagLink>
            <UiTagLink
              :searchscroll="true"
              v-if="isJps && item.common.provider"
              :url="providerSearch(item.common.provider)"
              text-overflow="wrap"
              styled="tertiary"
            >
              <UiOrgProviderSpan :id="item.common.provider" tag="span">
                <template #default="{ org }"
                  >{{ ls(dapLangGlobal.provider) }}:{{ ls(org.name) }}</template
                >
              </UiOrgProviderSpan>
            </UiTagLink>
            <UiTagLink
              :searchscroll="true"
              :url="dbSearch(db)"
              text-overflow="wrap"
              styled="tertiary"
            >
              {{ lss('収録', 'Database') }}:{{ ls(db.name) }}
            </UiTagLink>
            <BaseLink
              :searchscroll="true"
              class="jps-tag jps-blue"
              v-if="item.common.thumbnailUrl"
              :to="searchTo({ csid: systemCrossSearchId(), image: [item.id] })"
            >
              <BaseIcon class="is-size-6" name="mdi/image-search-outline" />
              {{ ls(dapLangGlobal.item.imageSearch) }}
            </BaseLink>
          </div>

          <UiItemMetacommon
            v-if="label"
            :label="label"
            :item="item"
            class="jps-item-meta-list ui-item-view-meta-list"
          />

          <UiInteractPanel type="item" :data="item" :image="image">
            <a
              v-if="hasRDF && !isHina"
              title="RDF"
              :href="'/data/' + item.id"
              class="jps-interact-icon"
              aria-label="RDF"
            >
              <BaseIcon name="icon/rdf-logo" />
            </a>
            <div class="spacer ui-item-view-spacer" />
            <button
              ref="showCitationPopupButton"
              :title="lss('引用', 'Citation')"
              class="jps-icon-button jps-blue jps-interact-icon"
              :aria-label="lss('引用', 'Citation')"
              @click="showCitationPopup"
            >
              <BaseIcon name="mdi/comment-quote-outline" />
            </button>
          </UiInteractPanel>
          <UiContentTooltip ref="contentTooltip" :other-toggle="showCitationPopupButton">
            <Citation :data="item" type="item" :variation="true" />
          </UiContentTooltip>
        </div>
      </BaseContainer>
    </header>
    <BaseContainer
      tag="section"
      inner-class="jps-item-detail-description ui-item-view-detail-description"
      v-if="item.common.description || item.common.descriptionEn"
    >
      <h2 class="jps-is-hidden">
        {{ ls({ ja: '解説', en: 'Description' }) }}
      </h2>
      <p v-shtml="lss(item.common.description, item.common.descriptionEn)"></p>
    </BaseContainer>

    <UiItemMetatable :item="item" link class="jps-item-metatables ui-item-view-metatables" />

    <section class="jps-item-detail-usage">
      <!-- コンテンツライツ -->
      <UiRightsSummary
        :title="rightsSummaryTitle"
        :rights="item.common.contentsRightsType"
        :rights-description="db.contentsRights"
        :hide-license="hideLicense"
        type="item"
        :data="item"
      />
    </section>

    <BaseContainer tag="div">
      <ClientOnly>
        <!-- 高負荷になるためコメントアウト -->
<!--        <UiItemRdf-->
<!--          v-if="isJps && !isBot()"-->
<!--          class="jps-item-related-items"-->
<!--          :item="item"-->
<!--        />-->
      </ClientOnly>

      <BaseSectionContainer
        v-if="db"
        tag="section"
        size="middle"
        class="ui-item-view-database"
      >
        <h2
          class="ui-item-view-database-heading jps-section-heading heading-datasource"
        >
          {{ ls(dapLangGlobal.datasource) }}
        </h2>
        <UiDataPanel :data="db" type="database" :image-size="100" class="ui-item-view-data-panel" />
      </BaseSectionContainer>

      <section v-if="!isHina" class="ui-item-view-provider">
        <UiOrgFooter
          :provider-id="item.common.provider"
          :provider-link="item.common.providerUrl"
          :org-id="item.common.ownerOrg"
        />
      </section>

      <section class="ui-item-view-last-updated">
        <h2
          class="jps-section-heading heading-page-metadata heading-page-last-updated"
        >
          {{ ls(dapLangGlobal.lastUpdated) }}
        </h2>
        <p class="is-size-7 text-center">
          {{ ldate(item.common.lastUpdatedDate) }}
        </p>
      </section>
    </BaseContainer>
  </article>
</template>

<script lang="ts" setup>
//ItemDetailをjps-frameで直接Importすると、ItemDetailを直接表示した際、依存する他のコンポーネントがロードされないバグに対応するため、JpsFrameとItemDetailからItemViewを呼び出す実装に変更した。
import { dapLangGlobal } from '#imports'
import vShtml from '~/directives/shtml'
import type { UiContentTooltip } from '#components'

const { isHina, isJps } = useServiceType()
const config = useRuntimeConfig()
const { ls, lc, lss, ldate, li, isJa } = useI18n()
const { $getCodeDbCategory, $getCodeDigitalContentsType, $getCodeRdfType } = useNuxtApp()
const route = useRoute()
const quickLinkStore = useQuickLinkStore()
const { itemDetailNoImage } = useNoImage()

interface Props {
  item: Item
  test?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  test: false,
})

const categoryCode: Code = $getCodeDbCategory()
const digitalContentsTypeCode: Code = $getCodeDigitalContentsType()
const rdfTypeCode: Code = $getCodeRdfType()

const dataStore = useDataStore()

//マイノートに追加するイメージの設定
const iiifUrl = ref<string>(null)
const setUrl = url => {
  iiifUrl.value = url
}

const image = computed<ImageSelection>(() => {
  if (props.item && iiifUrl.value) {
    if (props.item.common.iiifUrl) {
      return {
        imgType: 'IIIF',
        manifestUrl: props.item.common.iiifUrl,
        infoJsonUrl: iiifUrl.value,
      }
    } else {
      return {
        imgType: 'ITEM',
        itemIndex: '' + props.item.common.contentsUrl.indexOf(iiifUrl.value),
        url: iiifUrl.value,
      }
    }
  } else {
    return null
  }
})

const showDbNotification = computed(() => {
  return db.value && !isEmpty(db.value.notification)
})

const hasImage = computed(() => {
  return (
    !isEmptyArray(props.item.common.thumbnailUrl) ||
    (props.item.common.contentsType === 'image' &&
      !isEmptyArray(props.item.common.contentsUrl)) ||
    props.item.common.iiifUrl
  )
})

const db = computed(() => {
  if (props.item) return dataStore.database(props.item.common.database)
  return null
})

const label = computed(() => {
  if (props.item) return dataStore.label(props.item.common.database)
  return null
})

const hasRDF = computed(() => {
  if (db.value) return db.value.rdfEnabled
  return false
})

const categorySearch = (c: string) => {
  return `/csearch/${systemCrossSearchId()}?csid=${systemCrossSearchId()}&f-cm=${c}`
}

const rdfTypes = computed(() => {
  return uniqueArray(
    props.item?.rdfindex?.type
      ?.map(str => encodeCode(rdfTypeCode, str)?.code)
      .filter(code => code != null) || [],
  )
})

const typeSearch = (type: string) => {
  return `/csearch/${systemCrossSearchId()}?csid=${systemCrossSearchId()}&f-type=${type}`
}

const providerSearch = (provider: string) => {
  return `/csearch/${systemCrossSearchId()}?csid=${systemCrossSearchId()}&f-common.provider=${provider}`
}

const dbSearch = (db: Database) => {
  return `/csearch/${systemCrossSearchId()}?csid=${systemCrossSearchId()}&f-db=${db.id}`
}

const showCitationPopupButton: Ref<HTMLElement> = ref()
const contentTooltip = ref<InstanceType<typeof UiContentTooltip>>()
const showCitationPopup = () => {
  contentTooltip.value.toggle()
}

const rightsSummaryTitle = computed(() => {
  if (props.item.common.contentsAccess === 'not_exist') {
    return {
      ja: 'この資料にはデジタルコンテンツが存在しません。',
      en: 'No Digital Content',
    }
  } else {
    return {
      ja: `どうやったらこの資料の${contentType.value}を使えるの？`,
      en: `CAN I USE IT?`,
    }
  }
})

const hideLicense = computed(() => {
  return props.item.common.contentsAccess === 'not_exist'
})

const contentType = computed(() => {
  return lc(
    digitalContentsTypeCode,
    props.item.common.contentsType,
    lss('デジタルコンテンツ', 'DIGITAL CONTENTS'),
  )
})

const viewerInfo = computed<ViewerInfo>(() => {
  if (props.item.common.iiifUrl) {
    return {
      type: 'MANIFEST',
      url: props.item.common.iiifUrl,
      firstPage: route.query.info as string,
    }
  } else if (
    props.item.common.contentsUrl &&
    props.item.common.contentsType === 'image'
  ) {
    if (props.item.common.contentsUrl.length > 1) {
      return {
        type: 'DIRECT',
        urls: props.item.common.contentsUrl,
        thumbnails: props.item.common.thumbnailUrl,
      }
    } else if (props.item.common.contentsUrl.length > 0) {
      return {
        type: 'DIRECT',
        url: props.item.common.contentsUrl[0],
      }
    }
  }

  return null
})

const isDigiDepoContent = computed(() => {
  return props.item?.common?.linkUrl?.startsWith(`${config.public.digiBaseUrl}/iframepid/kn/`) && props.item?.common?.contentsAccess === 'internet'
})

onMounted(() => {
  const database = props.item?.common?.database
  if (database) {
    quickLinkStore.quickLinkOptions.push({
      label: { ja: '同じデータベースを検索', en: 'Search same database' },
      toLink: (keyword) => {
        return `/database/${database}${toSearchQueryString({ csid: database + '-default', keyword: [keyword] })}`
      }
    })
  }
  const codeType = props.item?.rdfindex?.type[0]
  if (codeType) {
    const code = encodeCode(rdfTypeCode, codeType)?.code
    if (code) {
      quickLinkStore.quickLinkOptions.push({
        label: { ja: '同じ種類から検索', en: 'Search same type' },
        toLink: (keyword) => {
          return `/csearch/${systemCrossSearchId()}${toSearchQueryString({ csid: systemCrossSearchId(), keyword: [keyword], filter: { type: [code] } })}`
        }
      })
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.jps-item-detail {
  &.has-image {
    .jps-item-img {
      margin-top: 0.5rem;
    }
  }
  > header {
    overflow: hidden;
    .jps-item-img-container {
      position: relative;
      .jps-image-search {
        position: absolute;
        right: 0;
        bottom: 0;
      }
      background-color: $jps-gray-100;

      .jps-item-img {
        text-align: center;
        box-shadow:
          0 1.5px 2.5px rgba(0, 0, 0, 0.05),
          0 5px 5px rgba(0, 0, 0, 0.07),
          0 8px 11px rgba(0, 0, 0, 0.085),
          0 14px 14px rgba(0, 0, 0, 0.09);
      }
    }

    .jps-item-iframe-container {
      height: 640px;
      width: 100%;
      .jps-item-iframe {
        width: 100%;
        height: 100%;
      }
    }

    .jps-db-notification > div {
      justify-content: left;
      margin-top: calc(0.25rem * 2);
      margin-bottom: calc(0.25rem * 2);
      p,
      span {
        color: red;
      }
      p {
        margin-left: 0.75rem;
      }
    }
    .jps-item-information {
      .jps-itetitle {
        margin: 1rem 0;
      }
      .jps-item-lastupdate {
        margin: 2rem 0 1rem;
        color: $jps-gray-600;
        display: block;
      }
      .jps-item-url {
        margin-top: 1rem;
        .button {
        }
      }
      .jps-item-accessibility {
        margin-top: 0rem;
        margin-top: 0.5rem;
        color: $jps-gray-600;
        span {
          line-height: 125%;
          display: inline-block;
        }
      }
      .jps-item-meta-list {
        margin: 1rem 0;
        padding: 0 0.75rem;
      }
    }
  }

  .jps-item-detail-usage {
      margin-top: 1rem;
      padding: 0.75rem 0 0;
      @include is-tablet {
        margin-top: 3rem;
        margin-bottom: 3rem;
      }
  }

  section:not(.jps-item-detail-usage):not(.jps-item-related-items),
  .jps-item-information,
  .jps-db-notification > div {
    margin-top: calc(0.25rem * 4);
    margin-bottom: calc(0.25rem * 4);
    dl.jps-meta.provider {
      border-bottom-width: 2px;
      border-color: $jps-gray-300;
    }
    dl.jps-meta:nth-of-type(1) dt:first-child {
      color: black;
      padding: 1rem 0 0.5rem;
      border-top: none;
    }
    dl.jps-meta dt {
      padding-top: 1rem;
      border-top-width: 1px;
      border-color: $jps-gray-300;
      &:first-child {
        border-top: none;
      }
    }
    dl.jps-meta dd {
      padding-bottom: 1rem;
    }
    @include is-tablet {
      margin-top: calc(0.25rem * 12);
      margin-bottom: calc(0.25rem * 12);
    }
  }
  .jps-item-metatables {
    margin-top: 1rem !important;
    padding: 0.75rem;
    padding-top: 0 !important;
  }
  .jps-db-notification,
  .jps-item-detaiapi {
    margin-bottom: 1rem !important;
    padding-bottom: 0 !important;
    ul {
      display: flex;
      justify-content: center;
      margin-top: 1rem;
    }
    li:not(:last-child) a {
      margin-right: 1.5rem;
    }
    a {
      img {
        min-width: 1.5rem;
        max-height: 1.5rem;
      }
    }
  }
  .jps-interact-panel > * {
    padding-left: 0;
  }
//  .jps-item-related-items {
//    max-width: lcontent-width;
//    margin: auto;
//  }
  .jps-interact-panel {
    padding: 0;
    justify-content: center;
    .spacer {
      width: 0.5rem !important;
    }
  }
  .tag {
    border: none;
  }
}

.ui-item-view-container {
  position: relative;
  align-items: center;
  background-color: $jps-gray-100;
  padding-bottom: 0.625rem;
}
.ui-item-view-db-notification {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}
.ui-item-view-db-notification-item {
  display: flex;
  justify-content: flex-start;
}
.ui-item-view-db-notification-text {
  margin-left: 0.75rem;
  color: $jps-key-red;
}
.ui-item-view-information-header {
  margin-top: 1rem;
  margin-bottom: 1rem;
}
.ui-item-view-information-url {
  margin-top: 1rem;
}
.ui-item-view-meta-list {
  margin-top: 1rem;
  margin-bottom: 1rem;
  padding-left: 0.75rem;
  padding-right: 0.75rem;
}
.ui-item-view-spacer {
  width: 0.5rem;
}
.ui-item-view-detail-description {
  margin-top: 1rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  @include is-mobile {
    margin-top: 3rem;
    margin-bottom: 3rem;
  }
}
.ui-item-view-metatables {
    margin-top: 1rem;
    padding-left: 0.75rem;
    padding-right: 0.75rem;
}

.jps-item-related-items, .ui-item-view-database {
  margin: {
    top: 1rem;
    bottom: 1rem;
  }
  padding: 0.75rem;
  @include is-tablet {
    margin-top: 3rem;
    margin-bottom: 3rem;
  }
}

.ui-item-view-database-heading {
  @include is-mobile {
    margin-top: 0;
  }
}

.ui-item-view-provider {
  margin-top: 1rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  @include is-tablet {
    margin-top: 3rem;
    margin-bottom: 3rem;
  }
}
.ui-item-view-last-updated {
  margin-top: 1rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  @include is-tablet {
    margin-top: 3rem;
    margin-bottom: 3rem;
  }
}

.page-hina {
  .ui-item-view-data-panel {
    border-radius: 1rem;
    border-color: $hina-separator-dark;

    :deep(.jps-item-details) {
      color: $hina-text-sub;
    }
  }
}
</style>